import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDaysIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, Clock3Icon, CopyIcon, DownloadIcon, MenuIcon, PlusIcon, SendIcon, UsersRoundIcon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { EntryNoteDialog } from '../components/timesheets/EntryNoteDialog';
import { PinnedAllocationDialog } from '../components/timesheets/PinnedAllocationDialog';
import { RemoveEntryTypeDialog } from '../components/timesheets/RemoveEntryTypeDialog';
import { TimesheetGrid } from '../components/timesheets/TimesheetGrid';
import { emptyHours, getRowTotal, parseHours, WEEKDAY_KEYS, type DayKey, type ProjectOption, type TimesheetAllocationTemplate, type TimesheetRow } from '../data/timesheetTypes';
import { useSettingsStore } from '../data/settingsStore';
import { Sidebar } from '../components/Sidebar';
import { INITIAL_JOBS } from './Jobs';
import { useScreenInit } from '../useScreenInit';
type Feedback = {
  tone: 'saved' | 'submitted';
  message: string;
};
type SubmittedWeek = {
  submittedAt: string;
  totalHours: number;
};
type TimesheetState = {
  allocations: TimesheetAllocationTemplate[];
  weeklyRows: Record<string, TimesheetRow[]>;
  removedEntryTypes: Record<string, string>;
  submittedWeeks: Record<string, SubmittedWeek>;
};
type AllocationFields = Pick<TimesheetAllocationTemplate, 'billable' | 'projectId' | 'department' | 'category'>;
type SeedAllocation = AllocationFields & {
  notes: string;
  hours: Partial<Record<DayKey, string>>;
};
const DEFAULT_USER_ID = 'user-matthew';
const ACTIVE_PROJECTS: ProjectOption[] = INITIAL_JOBS.filter(job => job.status === 'ongoing').map(job => ({
  id: job.id,
  ref: job.ref,
  name: job.name,
  client: job.client,
  departments: job.departments
}));
export function Timesheets() {
  useScreenInit();
  const {
    departments,
    users
  } = useSettingsStore();
  const activeUsers = users.filter(user => user.status === 'active');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [isAddAllocationOpen, setIsAddAllocationOpen] = useState(false);
  const [entryNoteToEdit, setEntryNoteToEdit] = useState<TimesheetRow | null>(null);
  const [entryTypeToRemove, setEntryTypeToRemove] = useState<TimesheetRow | null>(null);
  const [selectedUserId, setSelectedUserId] = useState(DEFAULT_USER_ID);
  const [timesheetsByUser, setTimesheetsByUser] = useState<Record<string, TimesheetState>>(() => ({
    [DEFAULT_USER_ID]: createInitialTimesheetState(ACTIVE_PROJECTS)
  }));
  const selectedUser = activeUsers.find(user => user.id === selectedUserId) || activeUsers[0];
  const activeDepartments = departments.filter(department => department.status === 'active').map(department => department.name);
  const weekKey = dateKey(weekStart);
  const timesheetState = timesheetsByUser[selectedUser?.id || selectedUserId] || createInitialTimesheetState(ACTIVE_PROJECTS);
  const rows = timesheetState.weeklyRows[weekKey] || [];
  const submittedWeek = timesheetState.submittedWeeks[weekKey];
  const days = useMemo(() => createWeekDays(weekStart), [weekStart]);
  const dayTotals = days.map(day => rows.reduce((total, row) => total + parseHours(row.hours[day.key]), 0));
  const weeklyTotal = dayTotals.reduce((total, hours) => total + hours, 0);
  const updateSelectedTimesheet = (updater: (current: TimesheetState) => TimesheetState) => {
    const userId = selectedUser?.id || selectedUserId;
    setTimesheetsByUser(current => ({
      ...current,
      [userId]: updater(current[userId] || createInitialTimesheetState(ACTIVE_PROJECTS))
    }));
  };
  useEffect(() => {
    if (!selectedUser) return;
    setTimesheetsByUser(current => current[selectedUser.id] ? current : {
      ...current,
      [selectedUser.id]: createInitialTimesheetState(ACTIVE_PROJECTS)
    });
  }, [selectedUser]);
  useEffect(() => {
    updateSelectedTimesheet(current => {
      if (current.weeklyRows[weekKey]) return current;
      const generatedRows = current.allocations.filter(allocation => weekKey > allocation.startsFromWeek && !isEntryTypeRemoved(current.removedEntryTypes, allocation, weekKey)).map(allocation => createRow(allocation, '', {}, allocation.id));
      return {
        ...current,
        weeklyRows: {
          ...current.weeklyRows,
          [weekKey]: generatedRows
        }
      };
    });
  }, [weekKey, selectedUserId]);
  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(null), 3500);
    return () => window.clearTimeout(timeout);
  }, [feedback]);
  const selectUser = (userId: string) => {
    setSelectedUserId(userId);
    setEntryNoteToEdit(null);
    setEntryTypeToRemove(null);
    setFeedback(null);
  };
  const updateRows = (updater: (current: TimesheetRow[]) => TimesheetRow[]) => {
    updateSelectedTimesheet(current => ({
      ...current,
      weeklyRows: {
        ...current.weeklyRows,
        [weekKey]: updater(current.weeklyRows[weekKey] || [])
      }
    }));
    setFeedback({
      tone: 'saved',
      message: 'Saved locally'
    });
  };
  const addPinnedAllocation = (allocation: AllocationFields) => {
    const alreadyPinned = timesheetState.allocations.some(item => allocationKey(item) === allocationKey(allocation));
    if (alreadyPinned) {
      setFeedback({
        tone: 'saved',
        message: 'That pinned entry type is already available'
      });
      setIsAddAllocationOpen(false);
      return;
    }
    const pinnedAllocation: TimesheetAllocationTemplate = {
      id: createId(),
      ...allocation,
      startsFromWeek: weekKey
    };
    updateSelectedTimesheet(current => {
      const entryTypeKey = allocationKey(pinnedAllocation);
      const removedEntryTypes = {
        ...current.removedEntryTypes
      };
      delete removedEntryTypes[entryTypeKey];
      return {
        allocations: [...current.allocations, pinnedAllocation],
        removedEntryTypes,
        weeklyRows: {
          ...current.weeklyRows,
          [weekKey]: [...(current.weeklyRows[weekKey] || []), createRow(pinnedAllocation, '', {}, pinnedAllocation.id)]
        }
      };
    });
    setIsAddAllocationOpen(false);
    setFeedback({
      tone: 'saved',
      message: `Pinned ${allocation.billable ? 'billable' : 'non-billable'} entry type added with blank hours`
    });
  };
  const requestEditEntryNote = (rowId: string) => {
    const row = rows.find(item => item.id === rowId);
    if (row) setEntryNoteToEdit(row);
  };
  const requestRemoveEntryType = (rowId: string) => {
    const row = rows.find(item => item.id === rowId);
    if (row?.templateId) setEntryTypeToRemove(row);
  };
  const confirmRemoveEntryType = () => {
    if (!entryTypeToRemove?.templateId) return;
    const {
      id: rowId,
      templateId
    } = entryTypeToRemove;
    updateSelectedTimesheet(current => {
      const entryTypeKey = allocationKey(entryTypeToRemove);
      return {
        allocations: current.allocations.filter(allocation => allocation.id !== templateId),
        removedEntryTypes: {
          ...current.removedEntryTypes,
          [entryTypeKey]: weekKey
        },
        weeklyRows: Object.fromEntries(Object.entries(current.weeklyRows).map(([key, weekRows]) => {
          if (key === weekKey) return [key, weekRows.filter(item => item.id !== rowId)];
          if (key > weekKey) return [key, weekRows.filter(item => allocationKey(item) !== entryTypeKey)];
          return [key, weekRows.map(item => item.templateId === templateId ? {
            ...item,
            templateId: undefined
          } : item)];
        }))
      };
    });
    setEntryTypeToRemove(null);
    setFeedback({
      tone: 'saved',
      message: 'Entry type removed from this and future weeks'
    });
  };
  const copyPreviousWeek = () => {
    const previousWeekRows = timesheetState.weeklyRows[dateKey(shiftWeek(weekStart, -1))] || [];
    updateSelectedTimesheet(current => ({
      ...current,
      weeklyRows: {
        ...current.weeklyRows,
        [weekKey]: previousWeekRows.filter(row => !isEntryTypeRemoved(current.removedEntryTypes, row, weekKey)).map(row => ({
          ...row,
          id: createId(),
          hours: {
            ...row.hours
          }
        }))
      }
    }));
    setFeedback({
      tone: 'saved',
      message: 'Copied the previous week locally'
    });
  };
  const exportTimesheet = () => {
    downloadCsv<TimesheetRow>(`timesheet-${weekKey}.csv`, [{
      label: 'User',
      value: () => selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''
    }, {
      label: 'Week starting',
      value: () => weekKey
    }, {
      label: 'Entry type',
      value: row => row.billable ? ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.name || 'Project unavailable' : row.category
    }, {
      label: 'Billable',
      value: row => row.billable ? 'Yes' : 'No'
    }, {
      label: 'Project ref',
      value: row => ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.ref
    }, {
      label: 'Client',
      value: row => ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.client
    }, {
      label: 'Department',
      value: row => row.department
    }, {
      label: 'Notes',
      value: row => row.notes
    }, ...WEEKDAY_KEYS.map(day => ({
      label: day.charAt(0).toUpperCase() + day.slice(1),
      value: (row: TimesheetRow) => parseHours(row.hours[day])
    })), {
      label: 'Total hours',
      value: row => getRowTotal(row)
    }], rows);
  };
  return <div className="flex h-screen w-screen overflow-hidden bg-[#1F5C9E] font-sans" data-id="element-2114" data-mp="35suxodi25xjp">
    <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-2115" data-mp="31qq39cjscww0" />
    <main className="flex min-w-0 flex-1 overflow-hidden p-0 md:py-3 md:pr-3" data-id="element-2116" data-mp="3fpzlehrjy12m"><div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-xl" data-id="element-2117" data-mp="3frar4n4k5iwd">
      <header className="border-b border-slate-100 px-5 py-5 md:px-8 md:py-7" data-id="element-2118" data-mp="3aszfq7ym70c1"><button type="button" onClick={() => setSidebarOpen(true)} className="mb-4 flex items-center gap-2 text-[#14466B] md:hidden" aria-label="Open menu" data-id="element-2119" data-mp="3opcwtfz8kzpz"><MenuIcon className="h-6 w-6" data-id="element-2120" data-mp="2b3adceva1ru" /></button><div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between" data-id="element-2121" data-mp="2a1lo54xhgrn"><div className="flex items-start gap-3" data-id="element-2122" data-mp="3tgw1xa87ertb"><span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-2123" data-mp="3thxn47ixouor"><Clock3Icon className="h-5 w-5" data-id="element-2124" data-mp="c9iu2ubke8rb" /></span><div data-id="element-2125" data-mp="7apnzmylxk16"><h1 className="text-2xl font-bold text-slate-900" data-id="element-2126" data-mp="7apmtjmvfdzl">Time Sheets</h1><p className="mt-1 text-sm text-slate-500" data-id="element-2127" data-mp="78cvwgz4xtae">Log billable project time and non-billable leave or internal time.</p></div></div><div className="flex flex-wrap items-center gap-2" data-id="element-2128" data-mp="3q9ytzqljsw0x"><button type="button" onClick={() => setIsAddAllocationOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#6A9A24] px-3.5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#587F1D] focus:outline-none focus:ring-2 focus:ring-[#6A9A24]/30 focus:ring-offset-2" data-id="element-2129" data-mp="3q85owfql2jv1"><PlusIcon className="h-4 w-4" data-id="element-2130" data-mp="htdju6h6jww8" />Add new entry type</button><button type="button" onClick={copyPreviousWeek} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-2131" data-mp="cuajv0g24gv4"><CopyIcon className="h-4 w-4" data-id="element-2132" data-mp="2tjg3wgjntb4u" />Copy previous week</button><button type="button" onClick={exportTimesheet} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export this user's selected week as CSV" data-id="element-2133" data-mp="2tl0z88dssy3r"><DownloadIcon className="h-4 w-4" data-id="element-2134" data-mp="18zazf8izdqyj" />Export CSV</button></div></div></header>
      <section className="flex flex-1 flex-col overflow-y-auto px-5 py-5 md:px-8 md:py-6" data-id="element-2135" data-mp="1d1frw3xp0hjl"><div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-2136" data-mp="1huqxy2uokuag"><div className="inline-flex w-fit items-center rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-id="element-2137" data-mp="1mtlgd3oqkgu1"><button type="button" onClick={() => setWeekStart(current => shiftWeek(current, -1))} className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show previous week" data-id="element-2138" data-mp="1rqu21e93f301"><ChevronLeftIcon className="h-4 w-4" data-id="element-2139" data-mp="20q8rqbhfr9jh" /></button><div className="min-w-[220px] px-2 text-center" data-id="element-2140" data-mp="2e0c4vrf9l87"><p className="text-sm font-semibold text-slate-900" data-id="element-2141" data-mp="2f1kutkytvmr">{formatWeekLabel(weekStart)}</p><label className="mt-0.5 flex cursor-pointer items-center justify-center gap-1 text-xs text-[#14466B]" htmlFor="timesheet-user" data-id="element-2142" data-mp="6eu0trcov425"><UsersRoundIcon className="h-3.5 w-3.5" data-id="element-2143" data-mp="14qd0gctcj8kz" /><span className="sr-only" data-id="element-2144" data-mp="14repjl775vlp">Choose a user's timesheet</span><select id="timesheet-user" value={selectedUser?.id || ''} onChange={event => selectUser(event.target.value)} className="max-w-[168px] cursor-pointer appearance-none bg-transparent pr-1 text-center text-xs font-medium text-[#14466B] outline-none hover:underline focus:ring-2 focus:ring-blue-100" data-id="element-2145" data-mp="1iq5yyub46t3i">{activeUsers.map(user => <option key={user.id} value={user.id} data-id="element-2146" data-mp="1npqzh95xn9ic">{user.firstName} {user.lastName}'s timesheet</option>)}</select></label></div><button type="button" onClick={() => setWeekStart(current => shiftWeek(current, 1))} className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show next week" data-id="element-2147" data-mp="22d3m717h7lab"><ChevronRightIcon className="h-4 w-4" data-id="element-2148" data-mp="2bcqmtksdsn2b" /></button></div><span className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-id="element-2149" data-mp="26grwhrnhsuz9"><CalendarDaysIcon className="h-4 w-4 text-[#14466B]" data-id="element-2150" data-mp="2q2boxo9fed3h" /><strong className="font-semibold tabular-nums text-slate-900" data-id="element-2151" data-mp="2v0v8xj4glet7">{weeklyTotal}h</strong>combined this week</span></div>
        {submittedWeek && <div className="mb-4 flex flex-col gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900 sm:flex-row sm:items-center sm:justify-between" data-id="element-2152" data-mp="2h0kjmkiss8a5"><div className="flex items-start gap-2" data-id="element-2153" data-mp="279zley71d5rr"><CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" data-id="element-2154" data-mp="278xx3s10ems3" /><div data-id="element-2155" data-mp="2c3bws6mpfg2k"><p className="text-sm font-semibold" data-id="element-2156" data-mp="2c3bwe5isle21">Timesheet submitted</p><p className="mt-0.5 text-xs text-emerald-800" data-id="element-2157" data-mp="1xdczs76vyaoo">Submitted {formatSubmittedAt(submittedWeek.submittedAt)} · {submittedWeek.totalHours}h recorded for this week.</p></div></div><span className="text-xs font-medium text-emerald-700" data-id="element-2158" data-mp="229lfblozw5wd">{selectedUser ? `${selectedUser.firstName}'s timesheet` : 'Current timesheet'}</span></div>}
        <TimesheetGrid rows={rows} projects={ACTIVE_PROJECTS} days={days} onChangeHours={(rowId, day, value) => updateRows(current => current.map(row => row.id === rowId ? {
            ...row,
            hours: {
              ...row.hours,
              [day]: value
            }
          } : row))} onEditNote={requestEditEntryNote} onRemoveEntryType={requestRemoveEntryType} data-id="element-2159" data-mp="1t9yasr0yyjz3" />
        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-2160" data-mp="1bixmatt21gvs"><div className="min-h-5" aria-live="polite" data-id="element-2161" data-mp="17hce3lrgtqrx">{feedback && <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${feedback.tone === 'submitted' ? 'text-emerald-700' : 'text-slate-500'}`} data-id="element-2162" data-mp="12l3p7gnguj7k"><CheckCircle2Icon className="h-4 w-4" data-id="element-2163" data-mp="xnc1kytutj96" />{feedback.message}</span>}</div>{submittedWeek ? <span className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700" data-id="element-2164" data-mp="snz3tj1g2rrt"><CheckCircle2Icon className="h-4 w-4" data-id="element-2165" data-mp="2myw1zgiqn15z" />Submitted</span> : <button type="button" onClick={() => {
              updateSelectedTimesheet(current => ({
                ...current,
                submittedWeeks: {
                  ...current.submittedWeeks,
                  [weekKey]: {
                    submittedAt: new Date().toISOString(),
                    totalHours: weeklyTotal
                  }
                }
              }));
              setFeedback({
                tone: 'submitted',
                message: `${selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}'s` : 'This'} week submitted with ${weeklyTotal} combined hours`
              });
            }} disabled={weeklyTotal === 0} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#6A9A24] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#587F1D] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400" data-id="element-2166" data-mp="2iyc3o9mfhri9"><SendIcon className="h-4 w-4" data-id="element-2167" data-mp="3mya42kcn841i" />Submit week</button>}</div>
      </section>
    </div></main>
    <AnimatePresence data-id="element-2168" data-mp="3qzvnkojongko">{isAddAllocationOpen && <PinnedAllocationDialog projects={ACTIVE_PROJECTS} departments={activeDepartments} onClose={() => setIsAddAllocationOpen(false)} onSave={addPinnedAllocation} data-id="element-2169" data-mp="3vxdnxqjmwnh2" />}{entryNoteToEdit && <EntryNoteDialog entryDescription={entryNoteToEdit.billable ? `${ACTIVE_PROJECTS.find(project => project.id === entryNoteToEdit.projectId)?.name || 'Project unavailable'} · ${entryNoteToEdit.department || 'No department'}` : entryNoteToEdit.category || 'Non-billable'} initialNote={entryNoteToEdit.notes} onClose={() => setEntryNoteToEdit(null)} onSave={note => {
        updateRows(current => current.map(row => row.id === entryNoteToEdit.id ? {
          ...row,
          notes: note
        } : row));
        setEntryNoteToEdit(null);
      }} data-id="element-2170" data-mp="1b6mwh4m9o74j" />}{entryTypeToRemove && <RemoveEntryTypeDialog entryDescription={entryTypeToRemove.billable ? `${ACTIVE_PROJECTS.find(project => project.id === entryTypeToRemove.projectId)?.name || 'Project unavailable'} · ${entryTypeToRemove.department || 'No department'}` : entryTypeToRemove.category || 'Non-billable'} onClose={() => setEntryTypeToRemove(null)} onConfirm={confirmRemoveEntryType} data-id="element-2171" data-mp="2oiifyff986p3" />}</AnimatePresence>
  </div>;
}
function createInitialTimesheetState(projects: ProjectOption[]): TimesheetState {
  const thisWeek = startOfWeek(new Date());
  const previousWeek = shiftWeek(thisWeek, -1);
  const first = projects[0];
  const second = projects[1];
  const currentRows: SeedAllocation[] = [...(first ? [{
    billable: true,
    projectId: first.id,
    department: 'Surveying',
    notes: 'Site survey and preparation',
    hours: {
      mon: '4',
      tue: '7.5',
      wed: '6',
      thu: '7.5',
      fri: '4'
    }
  }] : []), ...(second ? [{
    billable: true,
    projectId: second.id,
    department: 'Design',
    notes: 'Design review',
    hours: {
      wed: '2',
      thu: '1.5',
      fri: '4'
    }
  }] : []), {
    billable: false,
    category: 'Admin',
    notes: 'Internal administration',
    hours: {
      mon: '4',
      wed: '2',
      thu: '1.5'
    }
  }, {
    billable: false,
    category: 'CPD',
    notes: '',
    hours: {}
  }, {
    billable: false,
    category: 'Sick leave',
    notes: '',
    hours: {}
  }, {
    billable: false,
    category: 'Annual leave',
    notes: '',
    hours: {}
  }];
  const pinnedProjectIds = new Set(currentRows.filter(row => row.billable).map(row => row.projectId));
  projects.forEach(project => {
    if (!pinnedProjectIds.has(project.id)) currentRows.push({
      billable: true,
      projectId: project.id,
      department: project.departments[0] || 'Project Delivery',
      notes: '',
      hours: {}
    });
  });
  const previousRows: SeedAllocation[] = [...(first ? [{
    billable: true,
    projectId: first.id,
    department: 'Surveying',
    notes: 'Survey coordination',
    hours: {
      mon: '7.5',
      tue: '7.5',
      wed: '7.5',
      thu: '7.5',
      fri: '4'
    }
  }] : []), {
    billable: false,
    category: 'Annual leave',
    notes: 'Annual leave',
    hours: {
      fri: '3.5'
    }
  }];
  const templatesByKey = new Map<string, TimesheetAllocationTemplate>();
  [...currentRows, ...previousRows].forEach(row => {
    const key = allocationKey(row);
    if (!templatesByKey.has(key)) templatesByKey.set(key, {
      id: createId(),
      billable: row.billable,
      projectId: row.projectId,
      department: row.department,
      category: row.category,
      startsFromWeek: dateKey(thisWeek)
    });
  });
  const templateIdFor = (row: SeedAllocation) => templatesByKey.get(allocationKey(row))?.id;
  return {
    allocations: Array.from(templatesByKey.values()),
    removedEntryTypes: {},
    submittedWeeks: {},
    weeklyRows: {
      [dateKey(thisWeek)]: currentRows.map(row => createRow(row, row.notes, row.hours, templateIdFor(row))),
      [dateKey(previousWeek)]: previousRows.map(row => createRow(row, row.notes, row.hours, templateIdFor(row)))
    }
  };
}
function createRow(allocation: AllocationFields, notes = '', hours: Partial<Record<DayKey, string>> = {}, templateId?: string): TimesheetRow {
  return {
    id: createId(),
    ...allocation,
    notes,
    hours: {
      ...emptyHours(),
      ...hours
    },
    templateId
  };
}
function allocationKey({
  billable,
  projectId,
  department,
  category
}: AllocationFields) {
  return billable ? `billable::${projectId || ''}::${department || ''}` : `non-billable::${category || ''}`;
}
function isEntryTypeRemoved(removedEntryTypes: Record<string, string>, entryType: AllocationFields, targetWeek: string) {
  const removedFromWeek = removedEntryTypes[allocationKey(entryType)];
  return Boolean(removedFromWeek && targetWeek >= removedFromWeek);
}
function createWeekDays(weekStart: Date) {
  const today = dateKey(new Date());
  return WEEKDAY_KEYS.map((key, index) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + index);
    return {
      key,
      shortLabel: date.toLocaleDateString('en-GB', {
        weekday: 'short'
      }),
      dateLabel: date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      }),
      isToday: dateKey(date) === today
    };
  });
}
function startOfWeek(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  next.setDate(next.getDate() + (day === 0 ? -6 : 1 - day));
  next.setHours(0, 0, 0, 0);
  return next;
}
function shiftWeek(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount * 7);
  return next;
}
function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function formatSubmittedAt(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
function formatWeekLabel(weekStart: Date) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
  const start = weekStart.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: sameMonth ? undefined : 'short',
    year: sameYear ? undefined : 'numeric'
  });
  const end = weekEnd.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return `${start} – ${end}`;
}
function createId() {
  return `time-${Math.random().toString(36).slice(2, 10)}`;
}